import { useSyncExternalStore } from "react";
import type { MouseEvent as ReactMouseEvent } from "react";
import type { InsightsPreviewMode } from "../../features/user/types";
import {
  useAdminPanelToggle,
  useAdminPanelToggleContextReady,
} from "../../utils/feature-toggle-utils";
import { createStoredOptInPreference } from "../../utils/stored-opt-in";
import {
  INSIGHTS_CLASSIC_PATH,
  INSIGHTS_PREVIEW_PATH,
} from "./insights-preview-paths";

export * from "./insights-preview-paths";
export const INSIGHTS_PREVIEW_OPT_IN_KEY = "insightsPreviewOptIn";

export type InsightsRouteVersion = "classic" | "preview";

const INSIGHTS_PREVIEW_OPT_IN_EVENT = "insights-preview-optin-change";

type InsightsPreviewStorage = Pick<
  Storage,
  "getItem" | "setItem" | "removeItem"
>;

type StorageProvider = () => InsightsPreviewStorage | undefined;

export type InsightsPreviewPreference = ReturnType<
  typeof createInsightsPreviewPreference
>;

export type InsightsPreviewRolloutState = {
  mode: InsightsPreviewMode;
  previewActive: boolean;
};

const getBrowserStorage: StorageProvider = () => window.localStorage;

function emitOptInChange() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(INSIGHTS_PREVIEW_OPT_IN_EVENT));
  }
}

export function createInsightsPreviewPreference(
  getStorage: StorageProvider = getBrowserStorage
) {
  return createStoredOptInPreference(
    INSIGHTS_PREVIEW_OPT_IN_KEY,
    { onChange: emitOptInChange },
    getStorage
  );
}

export const insightsPreviewPreference = createInsightsPreviewPreference();

export function resolveInsightsPreviewMode(featureToggles: {
  forceInsightsPreview: boolean;
}): InsightsPreviewMode {
  return featureToggles.forceInsightsPreview ? "forced_preview" : "rollout";
}

export function useInsightsPreviewMode(): InsightsPreviewMode | null {
  const toggleContextReady = useAdminPanelToggleContextReady();
  const forceInsightsPreview = useAdminPanelToggle("forceInsightsPreview");

  if (!toggleContextReady) return null;

  return resolveInsightsPreviewMode({ forceInsightsPreview });
}

export function resolveInsightsRoute(
  mode: InsightsPreviewMode,
  requestedVersion: InsightsRouteVersion,
  isOptedIn: boolean
): InsightsRouteVersion {
  if (mode === "forced_preview") return "preview";
  return requestedVersion === "preview" || isOptedIn ? "preview" : "classic";
}

export function isInsightsPreviewActive(
  mode: InsightsPreviewMode,
  isOptedIn: boolean
): boolean {
  return resolveInsightsRoute(mode, "classic", isOptedIn) === "preview";
}

export function insightsPath(subpath = "", previewActive = false): string {
  return `${previewActive ? INSIGHTS_PREVIEW_PATH : INSIGHTS_CLASSIC_PATH}${subpath}`;
}

export function insightsRouteLocation(
  pathname: string,
  location: Pick<Location, "search" | "hash">
) {
  return {
    pathname,
    search: location.search,
    hash: location.hash,
  };
}

export function optIntoInsightsPreview(
  pathname: string,
  location: Pick<Location, "search" | "hash">,
  preference: InsightsPreviewPreference = insightsPreviewPreference
) {
  preference.optIn();
  return `${pathname}${location.search}${location.hash}`;
}

export function optOutOfInsightsPreview(
  location: Pick<Location, "pathname" | "search" | "hash">,
  preference: InsightsPreviewPreference = insightsPreviewPreference
) {
  preference.optOut();
  return `${location.pathname.replace(
    INSIGHTS_PREVIEW_PATH,
    INSIGHTS_CLASSIC_PATH
  )}${location.search}${location.hash}`;
}

export function isInsightsSubpathActive(
  currentPath: string,
  subpath: string
): boolean {
  return (
    currentPath.includes(`${INSIGHTS_CLASSIC_PATH}${subpath}`) ||
    currentPath.includes(`${INSIGHTS_PREVIEW_PATH}${subpath}`)
  );
}

export function insightsNavigationMode(
  currentPathname: string,
  targetPath: string
): "full-page" | "client" {
  const fromPreview = currentPathname.startsWith(INSIGHTS_PREVIEW_PATH);
  const toPreview = targetPath.startsWith(INSIGHTS_PREVIEW_PATH);
  return fromPreview !== toPreview ? "full-page" : "client";
}

export function makeInsightsNavHandler(
  target: string,
  existingOnClick?: (e: ReactMouseEvent<HTMLAnchorElement>) => void
) {
  return (e: ReactMouseEvent<HTMLAnchorElement>) => {
    existingOnClick?.(e);
    if (
      insightsNavigationMode(window.location.pathname, target) === "full-page"
    ) {
      e.preventDefault();
      window.location.assign(target);
    }
  };
}

function subscribeToPreferenceChanges(onChange: () => void): () => void {
  if (typeof window === "undefined") return () => {};

  window.addEventListener(INSIGHTS_PREVIEW_OPT_IN_EVENT, onChange);
  window.addEventListener("storage", onChange);
  return () => {
    window.removeEventListener(INSIGHTS_PREVIEW_OPT_IN_EVENT, onChange);
    window.removeEventListener("storage", onChange);
  };
}

export function useInsightsPreviewRolloutState(
  preference: InsightsPreviewPreference = insightsPreviewPreference
): InsightsPreviewRolloutState {
  const mode = useInsightsPreviewMode();
  const isOptedIn = useSyncExternalStore(
    subscribeToPreferenceChanges,
    () => preference.isOptedIn(),
    () => false
  );

  if (!mode) return { mode: "rollout", previewActive: isOptedIn };

  const previewActive = isInsightsPreviewActive(mode, isOptedIn);
  return {
    mode,
    previewActive,
  };
}
